import type { QueryClient } from "@tanstack/react-query";
import { chatKeys } from "@/lib/queryKeys/chatKeys";
import { eventKeys } from "@/lib/queryKeys/eventKeys";
import { streamKeys } from "@/lib/queryKeys/streamKeys";
import { studyGroupKeys } from "@/lib/queryKeys/studyGroupKeys";

const isNumericIdentifier = (value: unknown): boolean => {
  if (typeof value === "number") {
    return Number.isFinite(value);
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed === "") {
      return false;
    }
    return Number.isFinite(Number(trimmed));
  }

  return false;
};

/**
 * クエリ無効化のヘルパー関数
 * メンテナンス性を向上させるため、無効化ロジックを集約
 */
export const queryInvalidationHelpers = {
  // スタディグループ関連の全クエリを無効化
  invalidateStudyGroupQueries: (
    queryClient: QueryClient,
    studyGroupId?: string | number,
  ) => {
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey[0];
        const isStudyGroupRelatedRoot =
          key === "studyGroups" ||
          key === "studyGroup" ||
          key === "studyGroupTags" ||
          studyGroupKeys.isMyStudyGroupsKey(query.queryKey) ||
          studyGroupKeys.isMembershipsKey(query.queryKey) ||
          studyGroupKeys.isAdminsKey(query.queryKey) ||
          studyGroupKeys.isBlockedUsersKey(query.queryKey) ||
          studyGroupKeys.isYourMembershipKey(query.queryKey) ||
          studyGroupKeys.isInviteUsersPolicyKey(query.queryKey);

        if (!isStudyGroupRelatedRoot) {
          return false;
        }

        // 特定のIDが指定されている場合は、そのIDに関連するもののみ
        const candidateStudyGroupId = query.queryKey[1];
        if (
          studyGroupId &&
          isNumericIdentifier(candidateStudyGroupId) &&
          String(candidateStudyGroupId) !== String(studyGroupId)
        ) {
          return false;
        }
        return true;
      },
    });
  },

  // myStudyGroupsクエリ（全ステータス）を無効化
  invalidateMyStudyGroupsQueries: (queryClient: QueryClient) => {
    queryClient.invalidateQueries({
      predicate: (query) => studyGroupKeys.isMyStudyGroupsKey(query.queryKey),
    });
  },

  // 招待関連の全クエリを無効化
  invalidateInvitationQueries: (queryClient: QueryClient) => {
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey[0] as string;
        return [
          "invitations",
          "pendingInvitations",
          "sentInvitations",
        ].includes(key);
      },
    });
  },

  // チャット関連の全クエリを無効化
  invalidateChatQueries: (queryClient: QueryClient, chatRoomId?: string) => {
    queryClient.invalidateQueries({
      predicate: (query) =>
        chatKeys.shouldInvalidateForRoom(query.queryKey, chatRoomId),
    });
  },

  // チャットルーム訪問時は既読バッジだけ更新する。本文は再取得しない。
  invalidateChatVisitQueries: (queryClient: QueryClient) => {
    queryClient.invalidateQueries({
      predicate: (query) => chatKeys.shouldInvalidateForVisit(query.queryKey),
    });
  },

  // 既存チャット検索（参加者ベース）を無効化
  invalidateExistingChatQueries: (queryClient: QueryClient) => {
    queryClient.invalidateQueries({
      predicate: (query) =>
        chatKeys.isExistingByParticipantsKey(query.queryKey),
    });
  },

  // チャットメンバーシップ変更時の無効化（メンバー追加、削除など）
  invalidateOnChatMembershipChange: (
    queryClient: QueryClient,
    chatRoomId?: string,
  ) => {
    queryInvalidationHelpers.invalidateChatQueries(queryClient, chatRoomId);
    queryInvalidationHelpers.invalidateExistingChatQueries(queryClient);
  },

  // メンバーシップ変更時の無効化（参加、脱退、招待受諾など）
  invalidateOnMembershipChange: (
    queryClient: QueryClient,
    studyGroupId?: string | number,
  ) => {
    // 従来の無効化処理
    queryInvalidationHelpers.invalidateStudyGroupQueries(
      queryClient,
      studyGroupId,
    );
    queryInvalidationHelpers.invalidateInvitationQueries(queryClient);

    // ★ 追加: myStudyGroups（全ステータス）の無効化
    queryInvalidationHelpers.invalidateMyStudyGroupsQueries(queryClient);

    // チャットクエリの無効化（招待メッセージが即座に表示されるように）
    queryInvalidationHelpers.invalidateChatQueries(queryClient);
  },

  // イベント関連の無効化
  invalidateEventQueries: (
    queryClient: QueryClient,
    options?: { containerId?: number | string },
  ) => {
    queryClient.invalidateQueries({
      predicate: (query) => {
        if (eventKeys.isGroupEventsKey(query.queryKey)) {
          if (options?.containerId == null) {
            return true;
          }
          return query.queryKey[1] === options.containerId;
        }
        return eventKeys.isEventListKey(query.queryKey);
      },
    });
  },

  // ファイル関連の無効化
  invalidateFileQueries: (queryClient: QueryClient) => {
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey[0] as string;
        return ["files", "folders", "fileBlob"].includes(key);
      },
    });
  },

  // ブロック／アンブロック時に表示から除外すべきデータ群の無効化
  invalidateOnUserBlockChange: (
    queryClient: QueryClient,
    options?: {
      userId?: number | string;
      invalidateUserInfo?: boolean;
    },
  ) => {
    const predicate = (query: { queryKey: readonly unknown[] }) => {
      if (!Array.isArray(query.queryKey) || query.queryKey.length === 0) {
        return false;
      }
      const key = query.queryKey[0];
      if (typeof key !== "string") return false;

      switch (key) {
        case "posts":
          return streamKeys.isPostsKey(query.queryKey);
        case "pinnedPosts":
          return streamKeys.isPinnedPostsKey(query.queryKey);
        case "activities":
          return streamKeys.isActivitiesKey(query.queryKey);
        case "postDetail":
          return streamKeys.isPostDetailKey(query.queryKey);
        case "favoritePosts":
          return streamKeys.isFavoritePostsKey(query.queryKey);
        case "studyGroupMemberships":
          return studyGroupKeys.isMembershipsKey(query.queryKey);
        case "studyGroupAdmins":
          return studyGroupKeys.isAdminsKey(query.queryKey);
        default:
          return false;
      }
    };

    queryClient.invalidateQueries({ predicate });
    queryClient.refetchQueries({ predicate, type: "active" });

    if (options?.invalidateUserInfo) {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      queryClient.invalidateQueries({ queryKey: ["users", "current"] });
      queryClient.invalidateQueries({ queryKey: ["users", "profile"] });
      if (options.userId != null) {
        queryClient.invalidateQueries({
          queryKey: ["users", "detail", options.userId],
        });
      }
    }
  },
};
